import { FiberManualRecord } from '@mui/icons-material';
import { Box, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import CancelDialog from '@/src/components/Modal/Cancel/CancelDialog';
import { primaryOrange } from '@/src/styles/theme/lightThemeOptions';

import { internalServerMsg } from '../constants/CommonConstants';

type CustomizedDialogsProps = {
    open: boolean;
    handleClose: () => void;
    title: string;
    dialogContent: React.ReactElement;
    buttons: {
        label: string;
        variant: 'contained' | 'text' | 'outlined' | undefined;
        handleClick: () => void;
    }[];
    isErrorIconShow: boolean;
};
type ButtonDialogProps = {
    label: string;
    variant: 'contained' | 'text' | 'outlined' | undefined;
    handleClick: () => void;
};

const useErrorModalHooks = () => {
    const { t } = useTranslation(['modal', 'common']);
    const router = useRouter();
    const [showDialog, setShowDialog] = useState(false);
    const [responseErrorContent, setResponseErrorContent] = useState<any>(null);
    const [customButtonLabel, setCustomButtonLabel] = useState('');

    const customDialogContent = () => {
        if (!responseErrorContent) return <></>;

        const { error, message } = responseErrorContent;

        return (
            <Box>
                {error && (
                    <ListItem className="error-message">
                        <ListItemText
                            primary={error}
                            sx={{
                                fontWeight: 'bold',
                                padding: '0',
                            }}
                        />
                    </ListItem>
                )}
                {message && (
                    <ListItem className="error-message">
                        <ListItemIcon sx={{ minWidth: '20px' }}>
                            <FiberManualRecord
                                sx={{
                                    height: '10px',
                                    width: '10px',
                                    color: primaryOrange,
                                }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary={message}
                            sx={{
                                padding: '0',
                            }}
                        />
                    </ListItem>
                )}
            </Box>
        );
    };

    const openDialog = () => {
        setShowDialog(true);
    };

    const closeDialog = () => {
        if (responseErrorContent?.code === internalServerMsg) {
            setShowDialog(false);
            router?.reload();
        } else {
            setShowDialog(false);
        }
    };

    const isInternalServerError = responseErrorContent?.code === internalServerMsg;

    const commonButton: ButtonDialogProps = {
        label:
            customButtonLabel ||
            (isInternalServerError
                ? t('modal.button.error.cancel', { ns: 'common' })
                : t('modal.errormodal.button.OK', { ns: 'modal' })),
        variant: 'contained',
        handleClick: () => setShowDialog(false),
    };

    const errorModalButtons: ButtonDialogProps[] = isInternalServerError
        ? [
              {
                  label: customButtonLabel || t('modal.errormodal.button.no', { ns: 'modal' }),
                  variant: 'outlined',
                  handleClick: () => setShowDialog(false),
              },
              {
                  label: customButtonLabel || t('modal.errormodal.button.yes', { ns: 'modal' }),
                  variant: 'contained',
                  handleClick: closeDialog,
              },
          ]
        : [commonButton];

    const dialogProps: CustomizedDialogsProps = {
        open: showDialog,
        handleClose: () => setShowDialog(false),
        title: t('modal.systemerrormodal.title', { ns: 'modal' }),
        dialogContent: customDialogContent(),
        buttons: errorModalButtons,
        isErrorIconShow: false,
    };

    return {
        openDialog,
        setResponseErrorContent,
        setCustomButtonLabel,
        dialogComponent: <CancelDialog {...dialogProps} />,
    };
};

export default useErrorModalHooks;
